import { Typography } from '@mui/material';
import {
  type ExposureOrGroup,
  useUnderwritingQuestions,
  useUpdateUwQuestions,
} from 'api/submission';
import {
  APPLICATION_STEPS,
  BPP_AMOUNT_CODE,
  genericErrorMessage,
  HAS_BPP_CODE,
  HAS_SCHEDULED_BUILDINGS_CODE,
  HAS_TBI_BUILDING_CODE,
  HAS_TBI_CODE,
  ONLY_HOME_OFFICE_CODE,
  TBI_AMOUNT,
} from 'common/constants';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { displayToastMessage } from 'components/ToastMessage';
import { conditionReadsReference } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { isEmpty } from 'lodash-es';
import { useRef } from 'react';
import { useFullscreenLoader } from 'store/features/UI';
import type { ILocation } from '../Location/Location.types';
import type { IProperty } from './Properties.types';
import PropertyModal from './components/Modal/PropertyModal';
import type { PropertyModalRef } from './components/Modal/PropertyModal.types';

const category = 'property';

const Properties = () => {
  const { setFullscreenLoading } = useFullscreenLoader();

  const { relatedResource, performAction, submission, submissionId } =
    useExposuresOrGroupFields<IProperty>({
      type: 'exposure',
      name: 'building',
    });
  const { relatedResource: locationExposures } = useExposuresOrGroupFields<ILocation>({
    name: 'location',
    type: 'exposure',
  });

  const { mutateAsync: saveUnderwritingQuestions } = useUpdateUwQuestions(submissionId);
  const { data: questions } = useUnderwritingQuestions(submissionId, true);

  const propertyModalRef = useRef<PropertyModalRef>(undefined);

  const handleAdd = async (data: IProperty) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${data.bdg_name}`,
    });

  const handleUpdate = async (data: IProperty, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${data.bdg_name}`,
    });

  const handleDelete = async (property: ExposureOrGroup<IProperty>) =>
    performAction({
      action: 'delete',
      resourceId: property.id,
      loadingText: `Deleting the ${property.data.bdg_name}`,
    });

  // eslint-disable-next-line complexity
  const handleNext = async () => {
    if (relatedResource.length === 0) {
      displayToastMessage('ERROR', 'There must be at least one property.');
      return false;
    }

    const updatedUnderwritingQuestions = {};

    // Get all questions related to scheduled buildings
    const scheduledBuildingUWQuestionsCodes = (
      questions?.flattenQuestions?.find(
        (question) => question.code === APPLICATION_STEPS['uw-property'].key,
      )?.nested_fields ?? []
    )
      .filter((question) =>
        conditionReadsReference(question.show_condition, HAS_SCHEDULED_BUILDINGS_CODE),
      )
      .map((question) => question.code);

    /**
     * Clear all scheduled building related underwriting questions when hasScheduledBuildings is false
     */
    if (
      !submission?.data?.uw_questions?.[HAS_SCHEDULED_BUILDINGS_CODE] &&
      scheduledBuildingUWQuestionsCodes.length > 0
    ) {
      scheduledBuildingUWQuestionsCodes.forEach((code) => {
        if (submission?.data?.uw_questions?.[code]) {
          updatedUnderwritingQuestions[code] = '';
        }
      });
    }

    /**
     * if hasTBI has value and hasTBIBuildings is false, then set hasTBI to ''
     */
    if (
      !submission?.data?.uw_questions?.[HAS_TBI_BUILDING_CODE] &&
      submission?.data?.uw_questions?.[HAS_TBI_CODE]
    ) {
      updatedUnderwritingQuestions[HAS_TBI_CODE] = '';

      if (submission?.data?.uw_questions?.[TBI_AMOUNT]) {
        updatedUnderwritingQuestions[TBI_AMOUNT] = '';
      }
    }

    /**
     * if hasBPP has value and onlyHomeOffice is 'No', then set hasBPP to ''
     */
    if (
      submission?.data?.uw_questions?.[ONLY_HOME_OFFICE_CODE] === 'No' &&
      submission?.data?.uw_questions?.[HAS_BPP_CODE]
    ) {
      updatedUnderwritingQuestions[HAS_BPP_CODE] = '';

      if (submission?.data?.uw_questions?.[BPP_AMOUNT_CODE]) {
        updatedUnderwritingQuestions[BPP_AMOUNT_CODE] = '';
      }
    }

    if (!isEmpty(updatedUnderwritingQuestions)) {
      try {
        setFullscreenLoading(true, 'Saving data...');

        await saveUnderwritingQuestions({
          id: submissionId,
          body: {
            ...(submission?.data.uw_questions || {}),
            ...updatedUnderwritingQuestions,
          },
        });
      } catch (_err) {
        displayToastMessage('ERROR', genericErrorMessage);
        return false;
      } finally {
        setFullscreenLoading(false);
      }
    }

    return true;
  };

  const currentLocation = locationExposures.at(0)?.data;
  const constructionField = APPLICATION_STEPS.properties.fields.find(
    (field) => field.code === 'bdg_construction_code',
  );

  return (
    <>
      <PropertyModal
        ref={propertyModalRef}
        handleAdd={handleAdd}
        properties={relatedResource}
        handleUpdate={handleUpdate}
        currentLocation={currentLocation}
        policyData={submission?.data.policy_data}
      />
      <ExposuresWrapper
        title="Add your properties"
        description="Tell us about your buildings and work locations. Let’s get some details about the buildings you’re in."
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => propertyModalRef?.current?.openModal()}
        onNext={handleNext}
      >
        <ExposuresList>
          {relatedResource.map((property) => {
            const constructionCode = property.data.bdg_construction_code;
            const constructionName = constructionField?.choices?.find(
              (choice) => choice.code === constructionCode,
            );

            return (
              <ExposuresListItem
                key={property.id}
                title={`${property.data.bdg_name}`}
                category={category}
                onEdit={() => propertyModalRef.current?.openModal(property)}
                onDelete={() => {
                  handleDelete(property);
                }}
              >
                <Typography>{currentLocation?.loc_address_destructured?.street_line}</Typography>

                <Typography>
                  {currentLocation?.loc_address_destructured?.city},{' '}
                  {currentLocation?.loc_address_destructured?.state}{' '}
                  {currentLocation?.loc_address_destructured?.zipcode}
                </Typography>

                <Typography>
                  {property.data.bdg_sqft} sq ft. {constructionName?.name}
                </Typography>
              </ExposuresListItem>
            );
          })}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};

export default Properties;
